import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { LucideIcon, Plus, Car, Search, MoreVertical, Gauge, Eye, Wrench, ArrowRight, ArrowRightLeft, ArrowDownLeft, ArrowUpRight, Clock, CircleCheck, CircleX, Info, Tag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Vehicle, VehicleStatus } from '../../../../shared/types/vehicle.types';
import { VehicleTransfer, VehicleTransferStatus } from '../../../../shared/types/vehicle-transfer.types';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import VehiclePicture from '../../components/VehiclePicture';
import OdometerModal from '../../components/OdometerModal';
import {
    garageMenuIconStyles,
    garageStyles,
    getGarageStatusNoteLabelStyles,
    getGarageStatusNoteRowStyles,
    getTransferStatusColorStyles,
    incomingTransferDirectionStyles,
    outgoingTransferDirectionStyles,
} from './Garage.styles';

const statusColors: Record<VehicleStatus, 'success' | 'warning' | 'error' | 'info'> = {
    [VehicleStatus.Active]: 'success',
    [VehicleStatus.Pending]: 'warning',
    [VehicleStatus.Rejected]: 'error',
    [VehicleStatus.InfoRequested]: 'info',
};

const transferStatusConfig: Record<VehicleTransferStatus, { color: 'warning' | 'success' | 'error' | 'info'; Icon: LucideIcon }> = {
    pending: { color: 'warning', Icon: Clock },
    approved: { color: 'success', Icon: CircleCheck },
    rejected: { color: 'error', Icon: CircleX },
    infoRequested: { color: 'info', Icon: Info },
};

const Garage: React.FC = () => {
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const { list: listVehicles } = useVehicle();
    const { list: listTransfers } = useVehicleTransfer();
    const [machines, setMachines] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [transfers, setTransfers] = useState<VehicleTransfer[]>([]);
    const [transfersLoading, setTransfersLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
    const [odometerMachine, setOdometerMachine] = useState<Vehicle | null>(null);

    useEffect(() => {
        if (!user?.garageUid) {
            setLoading(false);
            return;
        }
        listVehicles({ garageUid: user.garageUid! }).then((data) => {
            setMachines(data);
            setLoading(false);
        });
    }, [user?.garageUid, listVehicles]);

    useEffect(() => {
        if (!user?.garageUid) {
            setTransfersLoading(false);
            return;
        }
        listTransfers({ garageUid: user.garageUid }).then((data) => {
            setTransfers(data.filter(t => t.status !== 'approved' && t.status !== 'rejected'));
            setTransfersLoading(false);
        });
    }, [user?.garageUid, listTransfers]);

    const filteredMachines = machines.filter(m =>
        m.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.vin.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const incomingTransfers = transfersLoading ? [] : transfers.filter(t =>
        t.toGarageUid === user?.garageUid &&
        (!searchTerm || t.vehicleUid.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const openOdometerModal = (machine: Vehicle) => {
        setOdometerMachine(machine);
        setMenuAnchor(null);
    };

    const menuMachine = machines.find(m => m.uid === menuAnchor?.id);

    return (
        <Box sx={garageStyles.root}>
            <Box sx={garageStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('garage.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('garage.subtitle')}</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Plus size={20} />}
                    component={Link}
                    to="/app/garage/add-machine"
                >
                    {t('garage.addMachine')}
                </Button>
            </Box>

            <Paper elevation={0} sx={garageStyles.searchPaper}>
                <TextField
                    size="small"
                    placeholder={t('garage.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={20} /></InputAdornment> }}
                    sx={garageStyles.searchField}
                />
            </Paper>

            {loading ? (
                <Box sx={garageStyles.loading}>
                    <CircularProgress />
                </Box>
            ) : filteredMachines.length === 0 && incomingTransfers.length === 0 ? (
                <Paper elevation={0} sx={garageStyles.emptyPaper}>
                    <Car size={48} color="#bdbdbd" />
                    <Typography variant="body2" fontWeight={500}>{t('garage.empty.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('garage.empty.description')}</Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {filteredMachines.map((machine) => {
                        const statusColor = statusColors[machine.status];
                        const outgoingTransfer = !transfersLoading
                            ? transfers.find(t => t.vehicleUid === machine.uid && t.fromGarageUid === user?.garageUid)
                            : undefined;
                        return (
                            <Grid item xs={12} md={6} key={machine.uid}>
                                <Paper elevation={0} sx={garageStyles.machineCard}>
                                    <Box sx={garageStyles.machineCardContent}>
                                        <Box sx={garageStyles.machineHeader}>
                                            <Box component={Link} to={`/app/vehicles/${machine.vin}`} sx={garageStyles.machineLink}>
                                                <Box sx={garageStyles.machineImage}>
                                                    <VehiclePicture vehicle={machine} imgSx={{ height: 64, objectFit: 'cover' }} />
                                                </Box>
                                                <Box>
                                                    {machine.nickname && (
                                                        <Typography variant="subtitle1" fontWeight={700}>{machine.nickname}</Typography>
                                                    )}
                                                    <Typography variant={machine.nickname ? 'body2' : 'subtitle1'} fontWeight={machine.nickname ? 400 : 700} color={machine.nickname ? 'text.secondary' : 'text.primary'}>{machine.model}</Typography>
                                                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">{machine.vin}</Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={garageStyles.machineHeaderActions}>
                                                {machine.availableForSale && (
                                                    <Chip label={t('garage.availableForSale')} color="info" size="small" icon={<Tag size={14} />} />
                                                )}
                                                <Chip label={t(`vehicleStatus.${machine.status}`)} color={statusColor} size="small" />
                                                <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: machine.uid })}>
                                                    <MoreVertical size={20} />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {machine.warranty && (
                                            <Box sx={garageStyles.warrantyRow}>
                                                <Typography variant="body2" color="text.secondary">{t('garage.warrantyUntil')}</Typography>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {formatDate(machine.warranty.validUntil)}
                                                </Typography>
                                            </Box>
                                        )}
                                        {(machine.status === VehicleStatus.InfoRequested || machine.status === VehicleStatus.Rejected) && machine.statusNote && (
                                            <Box sx={getGarageStatusNoteRowStyles(Boolean(machine.warranty))}>
                                                <Typography variant="body2" color={machine.status === VehicleStatus.Rejected ? 'error.main' : 'info.main'} fontWeight={500} sx={getGarageStatusNoteLabelStyles(machine.status)}>
                                                    {machine.status === VehicleStatus.Rejected ? t('garage.statusNote.rejected') : t('garage.statusNote.infoRequested')}
                                                </Typography>
                                                <Typography variant="body2" color={machine.status === VehicleStatus.Rejected ? 'error.main' : 'info.main'} textAlign="right">
                                                    {machine.statusNote}
                                                </Typography>
                                            </Box>
                                        )}

                                        {outgoingTransfer && (() => {
                                            const cfg = transferStatusConfig[outgoingTransfer.status ?? 'pending'];
                                            const StatusIcon = cfg.Icon;
                                            return (
                                                <Box
                                                    component={Link}
                                                    to={`/app/transfers/${outgoingTransfer.id}`}
                                                    sx={garageStyles.outgoingTransferLink}
                                                >
                                                    <Box sx={garageStyles.transferTopRow}>
                                                        <Box sx={garageStyles.transferLabelWithIcon}>
                                                            <ArrowRightLeft size={12} />
                                                            <Typography variant="body2" fontWeight={600} color="text.secondary">{t('garage.transfer.active')}</Typography>
                                                        </Box>
                                                        <ArrowRight size={14} color="#9e9e9e" />
                                                    </Box>
                                                    <Box sx={garageStyles.transferInfoRow}>
                                                        <Typography variant="body2" color="text.secondary">{t('garage.transfer.statusLabel')}</Typography>
                                                        <Box sx={getTransferStatusColorStyles(cfg.color)}>
                                                            <StatusIcon size={14} />
                                                            <Typography variant="body2" fontWeight={700} color={`${cfg.color}.main`}>{t(`vehicleTransferStatus.${outgoingTransfer.status ?? 'pending'}`)}</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={garageStyles.transferInfoRow}>
                                                        <Typography variant="body2" color="text.secondary">{t('garage.transfer.direction')}</Typography>
                                                        <Box sx={outgoingTransferDirectionStyles}>
                                                            <ArrowUpRight size={14} />
                                                            <Typography variant="body2" fontWeight={700} color="warning.main">{t('garage.transfer.outgoing')}</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={garageStyles.transferInfoRow}>
                                                        <Typography variant="body2" color="text.secondary">{t('garage.transfer.createdAt')}</Typography>
                                                        <Typography variant="body2" fontWeight={700}>{formatDate(outgoingTransfer.createdAt)}</Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })()}
                                    </Box>

                                    <Box sx={garageStyles.machineFooter}>
                                        <Button
                                            component={Link}
                                            to={`/app/vehicles/${machine.vin}`}
                                            endIcon={<ArrowRight size={20} />}
                                            size="small"
                                            sx={garageStyles.machineFooterButton}
                                        >
                                            {t('garage.viewDetail')}
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {incomingTransfers.map((transfer) => {
                        const statusCfg = transferStatusConfig[transfer.status ?? 'pending'];
                        const StatusIcon = statusCfg.Icon;
                        return (
                            <Grid item xs={12} md={6} key={transfer.id}>
                                <Paper
                                    component={Link}
                                    to={`/app/transfers/${transfer.id}`}
                                    elevation={0}
                                    sx={garageStyles.incomingTransferCard}
                                >
                                    <Box sx={garageStyles.incomingTransferHead}>
                                        <Box sx={garageStyles.incomingTransferHeadRow}>
                                            <Box sx={garageStyles.incomingTransferVehicle}>
                                                <Box sx={garageStyles.incomingTransferVehicleIcon}>
                                                    <ArrowRightLeft size={24} color="#0288d1" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={700}>{t('garage.transfer.vehicleTitle')}</Typography>
                                                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">{transfer.vehicleUid}</Typography>
                                                </Box>
                                            </Box>
                                            <Chip label={t('garage.transfer.chip')} size="small" color="info" />
                                        </Box>
                                    </Box>
                                    <Box sx={garageStyles.incomingTransferBody}>
                                        <Box sx={garageStyles.transferInfoRow}>
                                            <Typography variant="body2" color="text.secondary">{t('garage.transfer.statusLabel')}</Typography>
                                            <Box sx={getTransferStatusColorStyles(statusCfg.color)}>
                                                <StatusIcon size={14} />
                                                <Typography variant="body2" fontWeight={700} color={`${statusCfg.color}.main`}>{t(`vehicleTransferStatus.${transfer.status ?? 'pending'}`)}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={garageStyles.transferInfoRow}>
                                            <Typography variant="body2" color="text.secondary">{t('garage.transfer.direction')}</Typography>
                                            <Box sx={incomingTransferDirectionStyles}>
                                                <ArrowDownLeft size={14} />
                                                <Typography variant="body2" fontWeight={700} color="success.main">{t('garage.transfer.incoming')}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={garageStyles.transferInfoRow}>
                                            <Typography variant="body2" color="text.secondary">{t('garage.transfer.createdAt')}</Typography>
                                            <Typography variant="body2" fontWeight={700}>{formatDate(transfer.createdAt)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={garageStyles.incomingTransferFooter} />
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { if (menuMachine) openOdometerModal(menuMachine); }}>
                    <Gauge size={20} style={garageMenuIconStyles} /> {t('garage.menu.odometer')}
                </MenuItem>
                <MenuItem component={Link} to={`/app/vehicles/${menuMachine?.vin}`} onClick={() => setMenuAnchor(null)}>
                    <Eye size={20} style={garageMenuIconStyles} /> {t('garage.menu.viewDetail')}
                </MenuItem>
                {menuMachine?.status === VehicleStatus.Active && (
                    <MenuItem component={Link} to={`/app/vehicles/${menuMachine.vin}/service-request`} onClick={() => setMenuAnchor(null)}>
                        <Wrench size={20} style={garageMenuIconStyles} /> {t('garage.menu.orderService')}
                    </MenuItem>
                )}
            </Menu>

            <OdometerModal
                machine={odometerMachine}
                onClose={() => setOdometerMachine(null)}
                onSaved={(vehicleUid, odometer) => setMachines(prev => prev.map(m =>
                    m.uid === vehicleUid ? { ...m, odometer } : m
                ))}
            />
        </Box>
    );
};

export default Garage;
